import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar';
import { Avatar, Box, Typography, useTheme, IconButton, Tooltip, Menu as MuiMenu, MenuItem as MuiMenuItem } from '@mui/material';
import { MoreHoriz, Icon } from '../icons';
import { MenuOpen, Menu as MenuIcon } from '@mui/icons-material';
import { hexToRGBA } from '../../utils/hex-to-rgba';

import { useAuth } from "../../hooks/useAuth";

import './SidebarStyles.css'

const EditSidebar = ({ collapsed, setCollapsed }) => {
  const user = useSelector((state) => state.auth.user);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { group_code, project_code } = useParams();
  const [queryParams, setQueryParams] = useState({});
  const [hasImage, setHasImage] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  const auth = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setQueryParams({
      projectName: params.get("projectName"),
      groupName: params.get("groupName"),
      projectId: params.get("pid"),
      folder: params.get("folder"),
    });
  }, [location.search]);

  const handleMenuClick = (path) => {
    // 페이지 이동 시 group_code, project_code, query params 포함
    const params = new URLSearchParams({
      projectName: queryParams.projectName || "",
      groupName: queryParams.groupName || "",
      pid: queryParams.projectId || "",
    });
    if (path === "file" && queryParams.folder) {
      params.set("folder", queryParams.folder);
    }
    const url = `/edit/${path}/${group_code}/${project_code}?${params.toString()}`;
    navigate(url);
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };

  const sidebarTheme = theme.palette.mode === 'light' ? {
    sidebar: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    menu: {
      menuContent: theme.palette.background.default,
      icon: theme.palette.primary.main,
      hover: {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
      disabled: {
        color: theme.palette.action.disabled,
      },
    },
  } : {
    sidebar: {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
    menu: {
      menuContent: theme.palette.background.paper,
      icon: theme.palette.primary.light,
      hover: {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
      disabled: {
        color: theme.palette.action.disabled,
      },
    },
  };

  const menuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
    },
    icon: {
      color: sidebarTheme.menu.icon,
      '& svg': {
        width: '20px',
        height: '20px',
      },
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: '#b6b7b9',
    },
    subMenuContent: ({ level }) => ({
      backgroundColor:
        level === 0
          ? hexToRGBA(sidebarTheme.menu.menuContent, hasImage && !collapsed ? 0.4 : 1)
          : 'transparent',
    }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
      '&:hover': {
        backgroundColor: hexToRGBA(sidebarTheme.menu.hover.backgroundColor, hasImage ? 0.8 : 1),
        color: sidebarTheme.menu.hover.color,
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  if (!user) {
    return null;
  }

  return (
    <Sidebar
      collapsed={collapsed}
      toggled={toggled}
      onBackdropClick={() => setToggled(false)}
      backgroundColor={hexToRGBA(sidebarTheme.sidebar.backgroundColor, hasImage ? 0.9 : 1)}
      rootStyles={{
        height: '100%',
        color: sidebarTheme.sidebar.color,
        width: '240px',
        minWidth: '240px',
        borderRight: `1px solid ${theme.palette.divider}`,
        borderLeft: 'none',
        borderBlock: 'none'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ marginBottom: '15px' }}>
            <div style={{  marginBlock: '8px', marginInline: '30px', display: 'flex', alignItems: 'center' }}>
              <Typography
                fontWeight={600}
                style={{ fontSize: '14px', opacity: 0.7, letterSpacing: '0.5px' }}
              >
                파일
              </Typography>
            </div>
            <Menu menuItemStyles={menuItemStyles}>
              <MenuItem 
                onClick={() => handleMenuClick("file")}
                style={{
                  height: 'auto',
                  borderRadius: '6px',
                  marginBlock: '2px',
                  marginInline: '10px',
                  paddingBlock: '10px',
                  backgroundColor: location.pathname.startsWith('/edit/file') ? theme.palette.primary.lightOpacity : null,
                }}
              >
                <Icon 
                  name="folderIcon" 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    color: location.pathname.startsWith('/edit/file') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }} 
                />
                <Typography 
                  variant="caption" 
                  style={{ 
                    fontSize: '16px', 
                    fontWeight: 500, 
                    opacity: 0.9,
                    color: location.pathname.startsWith('/edit/file') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }}>
                  파일 관리
                </Typography>
              </MenuItem>
            </Menu>
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <div style={{ marginBlock: '8px', marginInline: '30px', display: 'flex', alignItems: 'center' }}>
              <Typography
                fontWeight={600}
                style={{ fontSize: '14px', opacity: 0.7, letterSpacing: '0.5px' }}
              >
                데이터베이스
              </Typography>
            </div>
            <Menu menuItemStyles={menuItemStyles}>
              <MenuItem
                onClick={() => handleMenuClick("machinestatus")}
                style={{
                  height: 'auto',
                  borderRadius: '6px',
                  marginBlock: '2px',
                  marginInline: '10px',
                  paddingBlock: '10px',
                  backgroundColor: location.pathname.startsWith('/edit/machinestatus') ? theme.palette.primary.lightOpacity : null,
                }}
              > 
                <Icon 
                  name="squareSettingsIcon" 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    color: location.pathname.startsWith('/edit/machinestatus') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }} 
                />
                <Typography 
                  variant="caption" 
                  style={{ 
                    fontSize: '16px', 
                    fontWeight: 500, 
                    opacity: 0.9,
                    color: location.pathname.startsWith('/edit/machinestatus') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }}>
                  장비 데이터
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => handleMenuClick("statusmaster")}
                style={{
                  height: 'auto',
                  borderRadius: '6px',
                  marginBlock: '2px',
                  marginInline: '10px',
                  paddingBlock: '10px',
                  backgroundColor: location.pathname.startsWith('/edit/statusmaster') ? theme.palette.primary.lightOpacity : null,
                }}
              > 
                <Icon 
                  name="squareSliderIcon" 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    color: location.pathname.startsWith('/edit/statusmaster') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }} 
                />
                <Typography 
                  variant="caption" 
                  style={{ 
                    fontSize: '16px', 
                    fontWeight: 500, 
                    opacity: 0.9,
                    color: location.pathname.startsWith('/edit/statusmaster') ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }}>
                  상태 유형
                </Typography>
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default EditSidebar;
